import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

interface AlertEvent {
  readonly id: string;
  readonly alertKey: string;
  readonly severity: 'info' | 'warning' | 'critical';
  readonly title: string;
  readonly body: string | null;
  readonly payload: Record<string, unknown> | null;
  readonly recipients: string[];
  readonly emailsSent: number;
  readonly dedupeKey: string | null;
  readonly createdAt: string;
}

const PAGE_LIMIT = 10;

const SEVERITY_FILTERS = [
  { label: 'Critical', value: 'critical' },
  { label: 'Warning', value: 'warning' },
  { label: 'Info', value: 'info' },
];

const SEVERITY_COLOR: Record<string, string> = {
  info: '#0ea5e9',
  warning: '#d97706',
  critical: '#dc2626',
};

/** AdminAlerts — paginated, searchable history of every alert the platform has fired. */
export default function AdminAlerts() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [items, setItems] = useState<AlertEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [severity, setSeverity] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const fetchAlerts = useCallback(async (p = 1, s = '', sev = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(PAGE_LIMIT) });
      if (s) params.set('search', s);
      if (sev) params.set('severity', sev);
      const data = await api<{ items: AlertEvent[]; total: number }>(`/api/admin/alerts?${params}`);
      if (signal?.aborted) return;
      setItems(data.items);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, []);

  useEffect(() => { const ac = new AbortController(); fetchAlerts(1, '', '', ac.signal); return () => ac.abort(); }, [fetchAlerts]);

  const handleSearch = () => fetchAlerts(1, search, severity);
  const handleFilter = (v: string) => { setSeverity(v); fetchAlerts(1, search, v); };
  const handlePage = (p: number) => fetchAlerts(p, search, severity);

  const csvEndpoint = `/api/admin/alerts?${new URLSearchParams({ ...(search && { search }), ...(severity && { severity }) })}`;

  const renderItem = ({ item }: { item: AlertEvent }) => {
    const color = SEVERITY_COLOR[item.severity] || colors.textLight;
    const isExpanded = expanded === item.id;
    return (
      <TouchableOpacity style={styles.row} activeOpacity={0.7} onPress={() => setExpanded(isExpanded ? null : item.id)}>
        <View style={[styles.dot, { backgroundColor: color }]} />
        <View style={styles.rowBody}>
          <View style={styles.rowHeader}>
            <Text style={[styles.severity, { color, backgroundColor: color + '18' }]}>
              {item.severity.toUpperCase()}
            </Text>
            <Text style={styles.title} numberOfLines={isExpanded ? undefined : 1}>{item.title}</Text>
            <Text style={styles.time}>{new Date(item.createdAt).toLocaleString()}</Text>
          </View>
          <Text style={styles.alertKey}>{item.alertKey}{item.emailsSent === 0 ? ' · muted' : ` · ${item.emailsSent} sent`}</Text>
          {isExpanded && (
            <View style={styles.detailBox}>
              {item.body ? <Text style={styles.body}>{item.body}</Text> : null}
              {item.recipients?.length > 0 && (
                <Text style={styles.metaLine}>Recipients: {item.recipients.join(', ')}</Text>
              )}
              {item.payload && Object.keys(item.payload).length > 0 && (
                <View style={styles.payloadBox}>
                  {Object.entries(item.payload).map(([k, v]) => (
                    <Text key={k} style={styles.payloadLine}>
                      <Text style={styles.payloadKey}>{k}:</Text> {formatVal(v)}
                    </Text>
                  ))}
                </View>
              )}
            </View>
          )}
        </View>
        <Ionicons name={isExpanded ? 'chevron-up' : 'chevron-down'} size={18} color={colors.textLight} />
      </TouchableOpacity>
    );
  };

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
        <AdminToolbar
          search={search} onSearchChange={setSearch} onSearch={handleSearch}
          total={total} page={page} limit={PAGE_LIMIT} onPageChange={handlePage}
          csvEndpoint={csvEndpoint} csvFilename="alerts.csv" label="alert"
          filterOptions={SEVERITY_FILTERS} activeFilter={severity} onFilterChange={handleFilter}
        />
        {loading ? (
          <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
        ) : (
          <FlatList ListFooterComponent={<PageFooter />}
            data={items}
            keyExtractor={(item) => item.id}
            renderItem={renderItem}
            ListEmptyComponent={<Text style={styles.empty}>No alerts found</Text>}
          />
        )}
      </ContentContainer>
    </View>
  );
}

function formatVal(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: c.borderLight, gap: spacing.sm },
  dot: { width: 8, height: 8, borderRadius: 4, marginTop: 6 },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  severity: { fontSize: 10, fontWeight: fontWeight.bold, paddingHorizontal: 6, paddingVertical: 2, borderRadius: 4, overflow: 'hidden' },
  title: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.text, flex: 1, minWidth: 100 },
  time: { fontSize: fontSize.xs, color: c.textLight },
  alertKey: { fontSize: fontSize.xs, color: c.textLight, fontFamily: 'monospace', marginTop: 2 },
  detailBox: { marginTop: 8, padding: spacing.sm, backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm },
  body: { fontSize: fontSize.sm, color: c.text, marginBottom: 6 },
  metaLine: { fontSize: fontSize.xs, color: c.textSecondary, marginBottom: 4 },
  payloadBox: { marginTop: 6, paddingTop: 6, borderTopWidth: 1, borderTopColor: c.borderLight },
  payloadLine: { fontSize: fontSize.xs, color: c.textSecondary, fontFamily: 'monospace', marginVertical: 1 },
  payloadKey: { color: c.text, fontWeight: fontWeight.semibold },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
